import { samplePreviewAvailable } from "./TroubleshootingUtils/apiEnvironment";
import React from "react";
import { isPrivateAddress } from "./TroubleshootingUtils/probeTargets";
import {
  Autocomplete,
  Box,
  FormControlLabel,
  MenuItem,
  Switch,
  TextField,
} from "@mui/material";
import { MULISH } from "../WireGuardTunnels/VpnDebugger/vpnDebugConstants";
import { useTroubleshootingStore } from "./troubleshootingStore/troubleshootingStore";
import { Hint, KIND_LABEL, SiteLink } from "./TroubleshootingUi";
import {
  ParentAccount,
  TroubleshootingSite,
  TroubleshootingTab,
} from "./troubleshootingInterfaces";
import {
  connectionKindForSite,
  parentAccountLabels,
  useScopedSites,
  useScopedTunnels,
  useTabParentAccounts,
  useTabSiteOptions,
} from "./TroubleshootingUtils/useScope";
import {
  SAMPLE_SCENARIOS,
  isSampleScenarioKey,
  sampleScenarioByKey,
} from "./TroubleshootingUtils/sampleScenarios";

/** Why a tab's site list can be empty — the picker says which, not "No options". */
const SITE_OPTIONS_EMPTY: Record<TroubleshootingTab, string> = {
  diagnosis: "No sites in this account.",
  connections:
    "No sites here. Sites that email images are on the SMTP Cameras tab.",
  wireguard: "No sites in this account use a WireGuard tunnel.",
  "smtp-cameras": "No sites here send images by email.",
  "alarm-receivers": "No sites in this account.",
  "fisheye-dewarp": "No sites in this account.",
};

/**
 * The site dropdown's "everything in this group" row, so widening back is a
 * visible choice rather than knowing to clear the field. The group dropdown
 * has no equivalent on purpose: a group is required before anything is
 * checked, because every account at once was too much to read at a glance.
 */
const ALL_ID = -1;
const allSitesOption = (groupName?: string): TroubleshootingSite => ({
  id: ALL_ID,
  name: groupName ? `All sites in ${groupName}` : "All sites in this group",
  groupId: ALL_ID,
});

const pickerSx = {
  "& .MuiInputBase-root": { ...MULISH, backgroundColor: "#fff" },
};

/**
 * Filter row: group, then site, then the sample scenario.
 *
 * The pair *is* the scope, and a **group is required**: checking every account
 * at once put too much on screen to read, so nothing is checked until one is
 * chosen. A group alone covers every site in it; adding a site narrows to that
 * one. A customer with a single account has it chosen for them.
 *
 * The site list is the one *this tab* can say something about — WireGuard
 * sites on the WireGuard tab, SMTP sites on the SMTP tab — because picking any
 * other only ever renders "nothing here", and it resets when you change tab
 * (see `setTab`) since a site valid on one tab usually is not on the next. The
 * group survives that change: it is the account you are working in, and it is
 * valid on every tab.
 *
 * The scope note under the site field says what we know about the selection —
 * counts across the account or the group, or how the one site connects and
 * where its alarms go — so a customer can confirm they are looking at the
 * right thing before they read a finding.
 */
export const TroubleshootingScopeBar = ({
  actions,
}: {
  /** Far-right slot: Run all checks and when it last ran. */
  actions?: React.ReactNode;
}) => {
  const {
    tab,
    scope,
    setScope,
    groupId,
    setGroupId,
    siteId,
    setSiteId,
    sites,
    accountsLoading,
    sitesLoading,
    sitesError,
    siteDetail,
    smtpSiteIds,
    receivers,
    sampleData,
    setSampleData,
    sampleScenario,
    setSampleScenario,
    tunnels,
    tunnelsError,
    siteToSite,
  } = useTroubleshootingStore();
  const scopedTunnels = useScopedTunnels();
  const scopedSites = useScopedSites();
  const tabSites = useTabSiteOptions(tab);
  const tabAccounts = useTabParentAccounts(tab);

  const accountLabels = React.useMemo(
    () => parentAccountLabels(tabAccounts),
    [tabAccounts]
  );
  const accountLabel = (account: ParentAccount) =>
    accountLabels.get(account.id) ?? account.name;

  const selected = sites.find((s) => s.id === siteId) || null;
  const selectedAccount =
    tabAccounts.find((account) => account.id === groupId) || null;

  // While customer/names/{id}/ is in flight the list is empty on purpose, so
  // MUI shows loadingText instead of a lone "All sites in …" row that reads as
  // "this group has no sites" (a 200-site group takes ~5 s to list).
  // With no site of the tab's kind the list stays empty too, so MUI shows
  // noOptionsText ("No sites … use a WireGuard tunnel") rather than only the
  // "All sites in …" row (QA, ENG-2187).
  const siteOptions = React.useMemo(() => {
    if (groupId == null || sitesLoading) return [];
    const inGroup = tabSites.filter((site) => site.groupId === groupId);
    if (inGroup.length === 0) return [];
    return [allSitesOption(selectedAccount?.name), ...inGroup];
  }, [tabSites, groupId, selectedAccount, sitesLoading]);

  // Clearing the group widens back to every group. Diagnosis alone keeps a
  // group required (the picker is not clearable there), because every
  // account's findings at once was too much to read.
  const chooseGroup = (account: ParentAccount | null) => {
    if (!account) {
      setGroupId(null);
      setSiteId(null);
      setScope("all");
      return;
    }
    setGroupId(account.id);
    const keepsSite = tabSites.some(
      (site) => site.id === siteId && site.groupId === account.id
    );
    if (!keepsSite) setSiteId(null);
    setScope(keepsSite ? "site" : "group");
  };

  const chooseSite = (site: TroubleshootingSite | null) => {
    if (!site || site.id === ALL_ID) {
      setSiteId(null);
      setScope("group");
      return;
    }
    setSiteId(site.id);
    setGroupId(site.groupId);
    setScope("site");
  };

  const receiverCount =
    receivers?.status === "ok" ? receivers.data.length : null;
  const s2sCount = siteToSite?.status === "ok" ? siteToSite.data.length : 0;

  let note: React.ReactNode;
  let groupNote: React.ReactNode = null;
  if (scope === "group" && sitesLoading) {
    note = `Loading sites in ${selectedAccount?.name ?? "this group"}…`;
  } else if (scope === "group" && sitesError) {
    // Say the list failed rather than "0 sites": an empty count over a failed
    // request is the wrong answer to "does this group have sites".
    note = `${selectedAccount?.name ?? "This group"}: ${sitesError}`;
  } else if (scope === "group") {
    const groupTunnels = scopedTunnels.length;
    const groupSmtp = scopedSites.filter((site) =>
      smtpSiteIds.includes(site.id)
    ).length;
    note = `${selectedAccount?.name ?? "This group"}: ${
      scopedSites.length
    } site${scopedSites.length === 1 ? "" : "s"}, ${
      // A failed tunnel list is not "0 VPN connections" (QA DG-27).
      tunnelsError
        ? "VPN connections unavailable"
        : `${groupTunnels} VPN connection${groupTunnels === 1 ? "" : "s"}`
    }, ${groupSmtp} SMTP site${groupSmtp === 1 ? "" : "s"}`;
  } else if (groupId == null) {
    const count = `${tabAccounts.length} group${
      tabAccounts.length === 1 ? "" : "s"
    }`;
    if (accountsLoading) {
      groupNote = "Loading your groups…";
      note = null;
    } else {
      groupNote = count;
      note =
        tab === "diagnosis"
          ? "Choose a group to check."
          : "All groups. Choose one to narrow down, then a site.";
    }
  } else if (selected && siteDetail && siteDetail.id === selected.id) {
    const kind = connectionKindForSite(siteDetail, smtpSiteIds);
    // How the site is reached, in words: a private address means a network
    // we route to (usually IPsec), a public one the internet.
    const reachHost = (
      siteDetail.server_ip ||
      siteDetail.management_server_ip ||
      ""
    ).trim();
    const via =
      kind === "wireguard"
        ? scopedTunnels.length
          ? `WireGuard tunnel${
              scopedTunnels.length > 1 ? "s" : ""
            } ${scopedTunnels.map((tunnel) => tunnel.name).join(", ")}`
          : "WireGuard (no tunnel's subnet holds this site's addresses)"
        : kind === "direct"
        ? !reachHost
          ? "an address we don't have"
          : isPrivateAddress(reachHost)
          ? `a private network (usually a site-to-site VPN) at ${reachHost}`
          : `the internet at ${reachHost}`
        : KIND_LABEL[kind];
    const recv =
      receivers?.status === "ok"
        ? receivers.data.find((r) => r.site_ids.includes(selected.id))
        : null;
    note = (
      <>
        <SiteLink siteId={selected.id}>{selected.name}</SiteLink>
        {`: connects via ${via}${
          siteDetail.integration_type_name
            ? `, integration ${siteDetail.integration_type_name}`
            : ""
        }${recv ? `, alarms to ${recv.label}` : ""}`}
      </>
    );
  } else if (selected) {
    note = `${selected.name}: loading…`;
  } else if (sitesLoading) {
    note = `Loading sites in ${selectedAccount?.name ?? "this group"}…`;
  } else {
    note = "Pick a site.";
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1.5,
        flexWrap: "wrap",
        width: "100%",
      }}
    >
      <Box sx={{ minWidth: { xs: "100%", sm: 240 }, flex: "1 1 240px" }}>
        <Autocomplete
          size="small"
          options={tabAccounts}
          getOptionLabel={accountLabel}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              {accountLabel(option)}
            </li>
          )}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          value={selectedAccount}
          loading={accountsLoading}
          disableClearable={tab === "diagnosis" && selectedAccount != null}
          onChange={(_, value) => chooseGroup(value)}
          data-testid="scope-group-picker"
          sx={{ width: "100%", ...pickerSx }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Choose a group"
              inputProps={{
                ...params.inputProps,
                "aria-label": "Parent group",
              }}
            />
          )}
        />
        {groupNote != null && (
          <Box sx={{ mt: 0.75 }}>
            <Hint sx={{ display: "block", whiteSpace: "normal" }}>
              {groupNote}
            </Hint>
          </Box>
        )}
      </Box>
      <Box sx={{ minWidth: { xs: "100%", sm: 300 }, flex: "1.4 1 320px" }}>
        <Autocomplete
          size="small"
          options={siteOptions}
          getOptionLabel={(option) => option.name}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              {option.name}
            </li>
          )}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          // Group scope shows as an empty field with an "All sites in …"
          // placeholder, not as a selected row: when the sentinel was the value,
          // the clear button and Backspace put the store back to the state it was
          // already in, so the text reappeared and the X looked broken.
          value={selected}
          loading={sitesLoading}
          loadingText="Loading sites…"
          disabled={groupId == null}
          disableClearable={false}
          onChange={(_, value) => chooseSite(value)}
          noOptionsText={sitesError ? sitesError : SITE_OPTIONS_EMPTY[tab]}
          data-testid="scope-site-picker"
          sx={{ width: "100%", ...pickerSx }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={allSitesOption(selectedAccount?.name).name}
              inputProps={{ ...params.inputProps, "aria-label": "Site" }}
            />
          )}
        />
        {note != null && (
          <Box sx={{ mt: 0.75 }}>
            <Hint sx={{ display: "block", whiteSpace: "normal" }}>{note}</Hint>
          </Box>
        )}
      </Box>
      {/* Never on a production build, and not in production mode: live data
          is the point there, and sample cards blended into it read as real
          (Jacob, 2026-09-27; K-14). */}
      {samplePreviewAvailable() && (
        <Box sx={{ minWidth: { xs: "100%", sm: 240 }, flex: "1 1 240px" }}>
          {/* Named scenarios for QA (ENG-2039): each feeds every sampled
              surface with one situation. */}
          <TextField
            select
            size="small"
            value={sampleScenario}
            onChange={(e) => {
              const next = e.target.value;
              if (isSampleScenarioKey(next)) setSampleScenario(next);
            }}
            disabled={!sampleData}
            inputProps={{ "aria-label": "Sample scenario" }}
            data-testid="sample-scenario-picker"
            title={sampleScenarioByKey(sampleScenario).description}
            sx={{ width: "100%", ...pickerSx }}
          >
            {SAMPLE_SCENARIOS.map((scenario) => (
              <MenuItem
                key={scenario.key}
                value={scenario.key}
                title={scenario.description}
              >
                {scenario.label}
              </MenuItem>
            ))}
          </TextField>
          <FormControlLabel
            sx={{ m: 0, mt: 0.5, ml: -0.5 }}
            control={
              <Switch
                size="small"
                checked={sampleData}
                onChange={(e) => setSampleData(e.target.checked)}
                inputProps={{ "aria-label": "Preview with sample data" }}
              />
            }
            label={<Hint>Preview with sample data</Hint>}
            title="Fills in surfaces whose API does not exist yet with illustrative data. Everything rendered from it is marked Sample."
          />
          {sampleData && (
            <Hint
              data-testid="sample-scenario-description"
              sx={{ display: "block", mt: 0.5 }}
            >
              {sampleScenarioByKey(sampleScenario).description}
            </Hint>
          )}
        </Box>
      )}
      {actions && (
        <Box sx={{ ml: "auto", flexShrink: 0, alignSelf: "flex-start" }}>
          {actions}
        </Box>
      )}
    </Box>
  );
};
